import { queryKeys } from "@/client";
import {
  type RealtimeChannel,
  type SupabaseClient,
} from "@supabase/supabase-js";
import { type QueryClient } from "@tanstack/react-query";
import { type EnqueueSnackbar } from "notistack";

// Define types for payload data

// Define type for directory matter
interface DirectoryMatterPayload {
  new: {
    id: number;
    directory_submission_id: number;
    firm_id: string;
    is_public: boolean;
    included_order: number;
    name: string;
    client_name: string | null;
    matter_description: string | null;
    value: string | null;
    cross_border: string | null;
    lead_partner_name: string | null;
    other_team_members: string | null;
    other_firms: string | null;
    date_of_completion: string | null;
    other_info: string | null;
    is_new: boolean;
    [key: string]: any;
  } | null;
  eventType: string;
  [key: string]: any;
}

/**
 * Sets up and manages real-time subscriptions for a directory submission
 */
export const setupSubmissionSubscriptions = (
  submissionId: string,
  supabase: SupabaseClient,
  queryClient: QueryClient,
  enqueueSnackbar: EnqueueSnackbar,
  callbacks: {
    onMatterAdded?: (matter: DirectoryMatterPayload["new"]) => void;
  } = {},
) => {
  const subscriptions: RealtimeChannel[] = [];

  // Convert to number for consistent filtering
  const numericSubmissionId = parseInt(submissionId, 10);

  if (isNaN(numericSubmissionId)) {
    console.error("Invalid submission ID for subscriptions:", submissionId);
    return {
      setupAllSubscriptions: async () => {},
      cleanup: () => {},
      checkAndReconnectSubscriptions: () => {},
    };
  }

  const subscribeToChannel = (channel: RealtimeChannel): Promise<void> => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Subscription timeout"));
      }, 10000); // 10 second timeout

      channel.subscribe((status, err) => {
        clearTimeout(timeout);
        if (status === "SUBSCRIBED") {
          resolve();
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          reject(err);
        }
      });
    });
  };

  const setupCommentsSubscription =
    async (): Promise<RealtimeChannel | null> => {
      try {
        const channel = supabase
          .channel(`submission-comments:${submissionId}`)
          .on(
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "directory_submission_question_comment",
              filter: `submission_id=eq.${numericSubmissionId}`,
            },
            (payload) => {
              void queryClient.invalidateQueries({
                queryKey: queryKeys.directorySubmissionComments(submissionId),
              });
            },
          );

        await subscribeToChannel(channel);
        return channel;
      } catch (error) {
        console.error("Error setting up comments subscription:", error);
        return null;
      }
    };

  const setupRevisionsSubscription =
    async (): Promise<RealtimeChannel | null> => {
      try {
        const channel = supabase
          .channel(`submission-revisions:${submissionId}`)
          .on(
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "directory_submission_question_revision_history",
              filter: `submission_id=eq.${numericSubmissionId}`,
            },
            (payload) => {
              void queryClient.invalidateQueries({
                queryKey: queryKeys.directorySubmissionRevisions(submissionId),
              });
            },
          );

        await subscribeToChannel(channel);
        return channel;
      } catch (error) {
        console.error("Error setting up revisions subscription:", error);
        return null;
      }
    };

  const setupMattersSubscription =
    async (): Promise<RealtimeChannel | null> => {
      try {
        const channel = supabase
          .channel(`directory-matters:${submissionId}`)
          .on(
            // @ts-expect-error - Supabase TS types incorrectly handle postgres_changes
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "directory_matter",
              filter: `directory_submission_id=eq.${numericSubmissionId}`,
            },
            (payload: DirectoryMatterPayload) => {
              // Force a complete invalidation and refetch
              void queryClient.invalidateQueries({
                queryKey: queryKeys.directorySubmission(submissionId),
                refetchType: "all",
              });

              // Also invalidate the entire submission
              void queryClient.refetchQueries({
                queryKey: queryKeys.directorySubmission(submissionId),
              });

              // Call the callback if provided
              if (callbacks.onMatterAdded && payload.new) {
                callbacks.onMatterAdded(payload.new);
              }

              enqueueSnackbar("A new directory matter has been added", {
                variant: "success",
                anchorOrigin: { horizontal: "right", vertical: "bottom" },
                autoHideDuration: 5000, // Extend duration
              });
            },
          );

        await subscribeToChannel(channel);
        return channel;
      } catch (error) {
        console.error("Error setting up matters subscription:", error);
        return null;
      }
    };

  const setupAllSubscriptions = async (): Promise<void> => {
    // Clean up any existing subscriptions
    cleanup();

    try {
      const [commentsChannel, revisionsChannel, mattersChannel] =
        await Promise.all([
          setupCommentsSubscription(),
          setupRevisionsSubscription(),
          setupMattersSubscription(),
        ]);

      if (commentsChannel) subscriptions.push(commentsChannel);
      if (revisionsChannel) subscriptions.push(revisionsChannel);
      if (mattersChannel) subscriptions.push(mattersChannel);
    } catch (error) {
      console.error("Error setting up subscriptions:", error);
    }
  };

  const cleanup = () => {
    subscriptions.forEach((channel) => {
      void supabase.removeChannel(channel);
    });
    subscriptions.length = 0;
  };

  const checkAndReconnectSubscriptions = () => {
    const channels = supabase.getChannels();
    const expectedChannels = [
      `submission-comments:${submissionId}`,
      `submission-revisions:${submissionId}`,
      `directory-matters:${submissionId}`,
    ];

    for (const channelName of expectedChannels) {
      const channelExists = channels.find(
        (channel) =>
          channel.topic === channelName && channel.state === "joined",
      );

      if (!channelExists) {
        void setupAllSubscriptions();
        break;
      }
    }
  };

  return {
    setupAllSubscriptions,
    cleanup,
    checkAndReconnectSubscriptions,
    // Add debugging utility
    debugSubscriptions: () => {
      const channels = supabase.getChannels();

      channels.forEach((channel, index) => {
        console.log(`Channel ${index + 1}:`, {
          topic: channel.topic,
          state: channel.state,
          bindings: channel.bindings.length,
        });
      });

      // Test connection
      const testChannel = supabase.channel("connection-test");
      void testChannel.subscribe((status) => {
        console.log("Connection test result:", status);
        void supabase.removeChannel(testChannel);
      });
    },
    // Monitor connection health
    startConnectionMonitoring: () => {
      const interval = setInterval(() => {
        const channels = supabase.getChannels();
        const activeChannels = channels.filter(
          (c) => c.state === "joined",
        ).length;
        const expectedChannels = 3; // comments, revisions, matters

        if (activeChannels < expectedChannels) {
          console.warn(
            `Connection issue detected: ${activeChannels}/${expectedChannels} channels active`,
          );
          checkAndReconnectSubscriptions();
        }
      }, 30000); // Check every 30 seconds

      return () => {
        clearInterval(interval);
      };
    },
  };
};
